import { Article, ErrorOutlined, TrendingUp } from '@mui/icons-material'
import { Chip, Link, Stack } from '@mui/material'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import type { ControllerDescription } from '@/features/controller/types'
import { makeControllerLogSearchUrl } from '@/features/controller/utils/coralogixLinks'
import { makeGrafanaLinkForControllersDashboard } from '@/features/controller/utils/grafanaLinks'
import type { Site } from '@/features/site/types/site'

function LogSearchChip({ icon, label, url }: Readonly<{ icon: ReactElement; label: string; url: string | null }>) {
  // A controller without an equipment number has no usable log filter, so offer no link at all.
  if (url === null) {
    return null
  }

  return (
    <Link href={url} rel="noreferrer" target="_blank">
      <Chip color="default" icon={icon} label={label} variant="outlined" />
    </Link>
  )
}

export interface ControllerObservabilityDetailsProps {
  // Only the equipment number is read, so a controller Device Manager does not know can be passed too
  controller: Pick<ControllerDescription, 'equipmentNumber'>
  site?: Site
}

export function ControllerObservabilityDetails({ controller, site }: Readonly<ControllerObservabilityDetailsProps>) {
  const { t } = useTranslation()

  const grafanaUrl = makeGrafanaLinkForControllersDashboard(site || null, [controller])

  const coralogixLogSearchUrl15m = makeControllerLogSearchUrl(controller.equipmentNumber, 'last-15-minutes')
  const coralogixLogSearchUrl2d = makeControllerLogSearchUrl(controller.equipmentNumber, 'last-2-days')
  const coralogixLogSearchUrlErrors1d = makeControllerLogSearchUrl(controller.equipmentNumber, 'last-24-hours', [
    '$m.severity == ERROR',
  ])

  // Without any of these the section would be a heading over an empty row. Checked across all
  // three rather than sampling one, so a future chip on a different precondition cannot desync the
  // gate from what it guards.
  const hasLogSearchLinks = [coralogixLogSearchUrl15m, coralogixLogSearchUrl2d, coralogixLogSearchUrlErrors1d].some(
    (url) => url !== null,
  )

  // Both halves hang on the same fact, so they go together. Hiding the log chips and keeping the
  // Grafana one would show a link that loads a dashboard filtered to nothing, beside a heading with
  // nothing under it.
  if (grafanaUrl === null && !hasLogSearchLinks) {
    return (
      <CustomTypography color="grey.500" variant="caption">
        {t('controllers.observability.none_available')}
      </CustomTypography>
    )
  }

  return (
    <Stack flexDirection="row">
      {grafanaUrl !== null && (
        <Stack flex={1}>
          <CustomTypography color="grey.500" variant="caption">
            {t('controllers.observability.metrics')}
          </CustomTypography>
          <Link href={grafanaUrl} rel="noreferrer" target="_blank">
            <Chip
              color="default"
              icon={<TrendingUp />}
              label={t('controllers.observability.grafana')}
              variant="outlined"
            />
          </Link>
        </Stack>
      )}
      {hasLogSearchLinks && (
        <Stack flex={3}>
          <CustomTypography color="grey.500" variant="caption">
            {t('controllers.observability.logs')}
          </CustomTypography>
          <Stack direction="row" gap={1}>
            <LogSearchChip
              icon={<Article />}
              label={t('controllers.observability.logs_15m')}
              url={coralogixLogSearchUrl15m}
            />
            <LogSearchChip
              icon={<Article />}
              label={t('controllers.observability.logs_2d')}
              url={coralogixLogSearchUrl2d}
            />
            <LogSearchChip
              icon={<ErrorOutlined />}
              label={t('controllers.observability.logs_errors_1d')}
              url={coralogixLogSearchUrlErrors1d}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
